import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useNavigate, Outlet } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProvider } from './contexts/AdminContext';
import { AdminThemeProvider } from './contexts/AdminThemeContext';
import { BillingProvider } from './contexts/BillingContext';
import { StorageProvider } from './contexts/StorageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { userService } from './services/firestore';
import {
  needsShopifyInstallWizard,
  requiresShopifyComplianceOnboarding,
} from './utils/shopifyComplianceOnboarding';
import { readShopifyInstallWizardState } from './utils/shopifyInstallWizardStorage';
import { getInstallWizardResumePath } from './utils/shopifyInstallWizardResume';
import {
  readShopifyInstallStorage,
  removeShopifyInstallStorage,
  SHOPIFY_INSTALL_STORAGE_KEYS,
} from './utils/shopifyInstallPersistence';

// ── Eagerly loaded — always needed (layout shells, auth guards, loading) ─────
import LoadingAnimation from './components/LoadingAnimation';
import MainLayout from './components/MainLayout';
import NotFound from './components/NotFound';
import SubdomainRouter from './components/SubdomainRouter';
import ClarityRouteTracker from './components/ClarityRouteTracker';
import NotificationPopup from './components/NotificationPopup';
import StickyFeedbackButton from './components/StickyFeedbackButton';
import SessionController from './components/SessionController';
import AutomationSchedulerInitializer from './components/AutomationSchedulerInitializer';
import OnboardingGuard from './components/OnboardingGuard';

// ── Lazily loaded — only fetched when the user navigates to the route ────────
const Login                          = lazy(() => import('./components/Login'));
const ShopifyAppWrapper              = lazy(() => import('./components/ShopifyAppWrapper'));
const Signup                         = lazy(() => import('./components/Signup'));
const Analytics                      = lazy(() => import('./components/Analytics'));
const CampaignPerformanceReportPage  = lazy(() => import('./components/CampaignPerformanceReportPage'));
const Profile                        = lazy(() => import('./components/Profile'));
const ActivityPage                   = lazy(() => import('./components/ActivityPage'));
const Settings                       = lazy(() => import('./components/Settings'));
const WorkspaceSettings              = lazy(() => import('./components/WorkspaceSettings'));
const CollectionManager              = lazy(() => import('./components/CollectionManager'));
const Users                          = lazy(() => import('./components/Users'));
const AccountSettings                = lazy(() => import('./components/AccountSettings'));
const DeveloperSettings              = lazy(() => import('./components/DeveloperSettings'));
const VerifyDomain                   = lazy(() => import('./components/VerifyDomain'));
const DomainDNSConfig                = lazy(() => import('./components/DomainDNSConfig'));
const WorkspaceVerifyEmailAddress    = lazy(() => import('./components/VerifyEmailAddress'));
const Rewards                        = lazy(() => import('./components/Rewards'));
const ContentManager                 = lazy(() => import('./components/ContentManager'));
const Contacts                       = lazy(() => import('./components/Contacts'));
const AddContact                     = lazy(() => import('./components/AddContact'));
const ContactView                    = lazy(() => import('./components/ContactView'));
const SegmentTemplateSelector        = lazy(() => import('./components/SegmentTemplateSelector'));
const SegmentsPage                   = lazy(() => import('./components/SegmentsPage'));
const ContactsTagsPage               = lazy(() => import('./components/ContactsTagsPage'));
const ContactsImportPage             = lazy(() => import('./components/ContactsImportPage'));
const ContactsExportPage             = lazy(() => import('./components/ContactsExportPage'));
const HygieneHub                     = lazy(() => import('./components/HygieneHub'));
const ContactsFieldsPage             = lazy(() => import('./components/ContactsFieldsPage'));
const SegmentBuilderPage             = lazy(() => import('./components/SegmentBuilderPage'));
const SignupForms                    = lazy(() => import('./components/SignupForms'));
const SignupFormBuilder              = lazy(() => import('./components/SignupFormBuilder'));
const SignupFormNamePage             = lazy(() => import('./components/SignupFormNamePage'));
const SignupFormTemplateSelection    = lazy(() => import('./components/SignupFormTemplateSelection'));
const PublicSignupForm               = lazy(() => import('./components/PublicSignupForm'));
const PublicEmailPreview             = lazy(() => import('./components/PublicEmailPreview'));
const ConfirmSubscription            = lazy(() => import('./components/ConfirmSubscription'));
const PublicUnsubscribe              = lazy(() => import('./components/PublicUnsubscribe'));
const Integration                    = lazy(() => import('./components/Integration'));
const IntegrationSetup               = lazy(() => import('./components/IntegrationSetup'));
const ZapierIntegrationPage          = lazy(() => import('./components/ZapierIntegrationPage'));
const OAuthCallback                  = lazy(() => import('./components/OAuthCallback'));
const ZapierOAuthAuthorize           = lazy(() => import('./components/ZapierOAuthAuthorize'));
const WooCommerceSetup               = lazy(() => import('./components/WooCommerceSetup'));
const TypeformSetup                  = lazy(() => import('./components/TypeformSetup'));
const OnboardingProfile              = lazy(() => import('./components/OnboardingProfile'));
const OnboardingWorkspace            = lazy(() => import('./components/OnboardingWorkspace'));
const OnboardingPlanRedirect         = lazy(() => import('./components/OnboardingPlanRedirect'));
const Checkout                       = lazy(() => import('./components/Checkout'));
const DodoCheckoutReturn             = lazy(() => import('./components/DodoCheckoutReturn'));
const WelcomePage                    = lazy(() => import('./components/WelcomePage'));
const WelcomeSignup                  = lazy(() => import('./components/WelcomeSignup'));
const ActiveSession                  = lazy(() => import('./components/ActiveSession'));
const LandingPages                   = lazy(() => import('./components/LandingPages'));
const LandingPagesNew                = lazy(() => import('./components/LandingPagesNew'));
const LandingPageDashboard           = lazy(() => import('./components/LandingPageDashboard'));
const EnhancedLandingPageBuilder     = lazy(() => import('./components/EnhancedLandingPageBuilder'));
const Campaigns                      = lazy(() => import('./components/Campaigns'));
const CampaignsCalendar              = lazy(() => import('./components/CampaignsCalendar'));
const CampaignSetup                  = lazy(() => import('./components/CampaignSetup'));
const CampaignCreateStart            = lazy(() => import('./components/CampaignCreateStart'));
const WhatsAppCampaignSetup          = lazy(() => import('./components/WhatsAppCampaignSetup'));
const WhatsAppSettings               = lazy(() => import('./components/WhatsAppSettings'));
const CampaignNamePage               = lazy(() => import('./components/CampaignNamePage'));
const HTMLEmailEditor                = lazy(() => import('./components/HTMLEmailEditor'));
const TemplateGallery                = lazy(() => import('./components/TemplateGallery'));
const CampaignDesignNew              = lazy(() => import('./components/CampaignDesignNew'));
const CampaignContent                = lazy(() => import('./components/CampaignContent'));
const CampaignAnalytics              = lazy(() => import('./components/CampaignAnalytics'));
const CampaignSend                   = lazy(() => import('./components/CampaignSend'));
const CampaignEdit                   = lazy(() => import('./components/CampaignEdit'));
const Automations                    = lazy(() => import('./components/Automations'));
const WhatsAppTemplates              = lazy(() => import('./components/WhatsAppTemplates'));
const AutomationTester               = lazy(() => import('./components/AutomationTester'));
const AutomationAnalytics            = lazy(() => import('./components/AutomationAnalytics'));
const AutomationTemplatesPage        = lazy(() => import('./components/AutomationTemplatesPage'));
const AutomationEditor               = lazy(() => import('./components/AutomationEditor'));
const AutomationCreatePage           = lazy(() => import('./components/AutomationCreatePage'));
const AdminDashboardLayout           = lazy(() => import('./components/AdminDashboardLayout'));
const AdminRouteWrapper              = lazy(() => import('./components/AdminRouteWrapper'));
const AdminLogin                     = lazy(() => import('./components/AdminLogin'));
const AdminIndex                     = lazy(() => import('./components/AdminIndex'));
const AdminTemplateBuilder           = lazy(() => import('./components/admin/AdminTemplateBuilder'));
const AdminRedirect                  = lazy(() => import('./components/AdminRedirect'));
const ShopifySignupRedirect          = lazy(() => import('./components/ShopifySignupRedirect'));
const ShopifyInstallWizardRoutes     = lazy(() => import('./components/shopifyInstallWizard/ShopifyInstallWizardRoutes'));
const ShopifyOnboardingAddress       = lazy(() => import('./components/ShopifyOnboardingAddress'));
const ShopifyOnboardingSync          = lazy(() => import('./components/ShopifyOnboardingSync'));
const StandaloneDNSChecker           = lazy(() => import('./components/StandaloneDNSChecker'));
const TemplatesGallery               = lazy(() => import('./components/TemplatesGallery'));
const TemplateEditor                 = lazy(() => import('./components/TemplateEditor'));
const TemplatePreview                = lazy(() => import('./components/TemplatePreview'));
const TemplateBuilder                = lazy(() => import('./components/TemplateBuilder'));
const UserContacts                   = lazy(() => import('./components/UserContacts'));
const ShopifyStoreProducts           = lazy(() => import('./components/ShopifyStoreProducts'));
const ShopifyCustomersPage           = lazy(() => import('./components/ShopifyCustomersPage'));
const ShopifyOrdersPage              = lazy(() => import('./components/ShopifyOrdersPage'));
const ShopifyAbandonedCartsPage      = lazy(() => import('./components/ShopifyAbandonedCartsPage'));
const UserDetailsPage                = lazy(() => import('./components/UserDetailsPage'));
const NotificationPage               = lazy(() => import('./components/NotificationPage'));
const ImportMapping                  = lazy(() => import('./components/ImportMapping'));
const EmailVerificationRequired      = lazy(() => import('./components/EmailVerificationRequired'));
const EmailVerificationConfirm       = lazy(() => import('./components/EmailVerificationConfirm'));
const PasswordResetConfirm           = lazy(() => import('./components/PasswordResetConfirm'));
const PasswordResetPasscode          = lazy(() => import('./components/PasswordResetPasscode'));
const ForgotPassword                 = lazy(() => import('./components/ForgotPassword'));
const LandingPage                    = lazy(() => import('./components/LandingPage'));
const FormStyleSelection             = lazy(() => import('./components/FormStyleSelection'));
const TemplateSelectionPage          = lazy(() => import('./components/TemplateSelectionPage'));
const FormContentConfig              = lazy(() => import('./components/FormContentConfig'));
const LandingPageTemplateSelection   = lazy(() => import('./components/LandingPageTemplateSelection'));
const LandingPageContentConfig       = lazy(() => import('./components/LandingPageContentConfig'));
const LandingPageBuilder             = lazy(() => import('./components/LandingPageBuilder'));
const LandingPageProjectSetup        = lazy(() => import('./components/LandingPageProjectSetup'));
const LandingPageEditorShell         = lazy(() => import('./components/LandingPageEditorShell'));
const ShopifyEmbeddedApp             = lazy(() => import('./components/ShopifyEmbeddedApp'));
const ShopifyBillingConfirm          = lazy(() => import('./components/ShopifyBillingConfirm'));
const AdminTemplateManager           = lazy(() => import('./components/AdminTemplateManager'));
const AdminAutomationTemplateManager = lazy(() => import('./components/admin/AdminAutomationTemplateManager'));
const AdminAutomationTemplateEditorPage = lazy(() => import('./components/admin/AdminAutomationTemplateEditorPage'));
const PlatformEmailComposer          = lazy(() => import('./components/admin/PlatformEmailComposer'));
const AdminCreateEmailTemplatePage   = lazy(() => import('./components/AdminCreateEmailTemplatePage'));
const AdminTemplateDetailsPage       = lazy(() => import('./components/AdminTemplateDetailsPage'));
const TemplateAuditPage              = lazy(() => import('./components/admin/TemplateAudit').then(m => ({ default: m.TemplateAudit })));
const SubdomainManager               = lazy(() => import('./components/SubdomainManager'));
const PublicLandingPageWrapper       = lazy(() => import('./components/PublicLandingPageWrapper'));
const LandingPagePreviewById         = lazy(() => import('./components/LandingPagePreview').then(m => ({ default: m.LandingPagePreviewById })));
const LandingPagePreviewBySlug       = lazy(() => import('./components/LandingPagePreview').then(m => ({ default: m.LandingPagePreviewBySlug })));
const SubdomainLandingPageBuilder    = lazy(() => import('./components/SubdomainLandingPageBuilder'));
const TrackingHandler                = lazy(() => import('./components/TrackingHandler'));
const ShopifyOnboardingBrandingPage  = lazy(() => import('./components/ShopifyOnboardingBrandingPage'));
const ShopifyIntegrationDetails      = lazy(() => import('./components/ShopifyIntegrationDetails'));
const ShopifyIntegrationEntry        = lazy(() => import('./components/ShopifyIntegrationEntry'));
const CampaignTrackingAnalytics      = lazy(() => import('./components/CampaignTrackingAnalytics'));
const CampaignSummary                = lazy(() => import('./components/CampaignSummary'));
const WorkspaceDetails               = lazy(() => import('./components/WorkspaceDetails'));
const WorkspaceDashboardV2           = lazy(() => import('./components/WorkspaceDashboardV2'));
const ApiPage                        = lazy(() => import('./components/ApiPage'));
const HelpCenter                     = lazy(() => import('./components/HelpCenter'));
const HelpArticleDetail              = lazy(() => import('./components/HelpArticleDetail'));
const HelpCategoryPage               = lazy(() => import('./components/HelpCategoryPage'));
const HelpSearchResults              = lazy(() => import('./components/HelpSearchResults'));
const TermsOfUsePage                 = lazy(() => import('./components/TermsOfUsePage'));
const PrivacyPolicyPage              = lazy(() => import('./components/PrivacyPolicyPage'));
const CancellationRefundPage         = lazy(() => import('./components/CancellationRefundPage'));
const ShippingPolicyPage             = lazy(() => import('./components/ShippingPolicyPage'));
const AntiSpamPolicyPage             = lazy(() => import('./components/AntiSpamPolicyPage'));
const StatusPage                     = lazy(() => import('./components/StatusPage'));
const PricingPage                    = lazy(() => import('./components/PricingPage'));
const LiveUserCheck                  = lazy(() => import('./components/LiveUserCheck'));

// Named export with default re-export wrapper for WorkspaceSenderSelection
const WorkspaceSenderSelectionLazy   = lazy(() =>
  import('./components/VerifyEmailAddress').then(m => ({ default: m.WorkspaceSenderSelection }))
);

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  return currentUser ? <>{children}</> : <Navigate to="/" replace />;
};

/**
 * ShopifyEmbeddedGuard ensures that when Scubamail is running inside a Shopify iFrame:
 * 1. The original "standalone" UI (Contacts, Campaigns, etc.) is INACCESSIBLE.
 * 2. Access is restricted ONLY to Shopify-specific pages and the initial signup flow.
 * 3. Any attempt to reach standalone pages redirects back to the Shopify Sync Overview.
 */
const ShopifyEmbeddedGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isShopifyEmbedded } = useWorkspace();
  const location = useLocation();

  // Define routes that are allowed inside the Shopify Admin iFrame
  const allowedShopifyRoutes = [
    '/shopify/embedded',
    '/shopify/billing-confirm',
    '/shopify/install',
    '/shopify/onboarding',   // address / sync / branding inside Admin iframe
    '/shopify/integration',
    '/oauth/callback/shopify',
    '/signup/shopify',
    '/signup', // generic signup + /signup/shopify (prefix match)
    '/verify-email',          // Allow email verification if needed during signup
    '/verify-email-confirm',
    '/login',                 // Re-auth for Shopify if needed
    '/forgot-password',
    '/terms-of-use',
    '/privacy-policy',
  ];

  const isAllowed = allowedShopifyRoutes.some(path => 
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  if (isShopifyEmbedded && !isAllowed) {
    // Prevent redirect loop if we are already on the target path (double check)
    if (location.pathname === '/shopify/embedded') {
        return <>{children}</>;
    }

    console.warn(`[ShopifyEmbeddedGuard] Attempted to reach standalone page '${location.pathname}' inside Shopify Admin. Redirecting to embedded dashboard.`);
    
    // Redirect to the embedded home. Keep search params (shop, host) to maintain context.
    return <Navigate to={`/shopify/embedded${location.search}`} replace />;
  }

  return <>{children}</>;
};



// Protected Route with Onboarding Guard (kept for onboarding routes)
// const ProtectedRouteWithOnboarding: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { currentUser } = useAuth();
//   if (!currentUser) {
//     return <Navigate to="/login" replace />;
//   }
//   return <OnboardingGuard>{children}</OnboardingGuard>;
// };

// Protected Route with Layout and Onboarding Guard
const ProtectedRouteWithLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  return (
    <OnboardingGuard>
      <MainLayout>
        {children}
      </MainLayout>
    </OnboardingGuard>
  );
};

const WorkspaceRouteRedirect: React.FC<{ resolvePath: (workspaceId: string, location: Location) => string; fallback?: string }> = ({ resolvePath, fallback = '/account/settings' }) => {
  const { currentWorkspace, loading } = useWorkspace();
  const location = useLocation();
  const workspaceId = currentWorkspace?.id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  if (workspaceId) {
    const target = resolvePath(workspaceId, location);
    return <Navigate to={target} replace />;
  }

  return <Navigate to={fallback} replace />;
};

const WorkspaceSettingsSendersIndexRedirect: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  if (!workspaceId) {
    return <Navigate to="/" replace />;
  }
  return <Navigate to={`/workspace/${workspaceId}/settings/senders/domains`} replace />;
};

// Redirect component for campaign routes that need to look up the campaign's workspace
const CampaignRouteRedirect: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const [redirecting, setRedirecting] = useState(true);

  useEffect(() => {
    const redirect = async () => {
      if (!campaignId) {
        // Wait for workspaces to load before deciding to fallback
        if (workspaceLoading) return;

        // No campaignId, redirect to workspace campaigns or fallback
        const workspaceId = currentWorkspace?.id;
        if (workspaceId) {
          navigate(`/workspace/${workspaceId}/campaigns`, { replace: true });
        } else {
          navigate('/account/settings', { replace: true });
        }
        return;
      }

      try {
        // Import and fetch campaign to get workspaceId
        const { getCampaign } = await import('./services/campaignService');
        const campaign = await getCampaign(campaignId);

        if (campaign && campaign.workspaceId) {
          // Extract the path after /campaigns/:campaignId
          const pathAfterCampaigns = location.pathname.split('/').slice(3).join('/');
          const newPath = `/workspace/${campaign.workspaceId}/campaigns/${campaignId}${pathAfterCampaigns ? '/' + pathAfterCampaigns : ''}`;
          navigate(newPath + (location.search || ''), { replace: true });
        }
 else {
          // Campaign not found or no workspaceId, redirect to workspace campaigns
          // Wait for workspaces to load before deciding to fallback
          if (workspaceLoading) return;

          const workspaceId = currentWorkspace?.id;
          if (workspaceId) {
            navigate(`/workspace/${workspaceId}/campaigns`, { replace: true });
          } else {
            navigate('/account/settings', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error fetching campaign for redirect:', error);
        // Fallback to workspace campaigns
        // Wait for workspaces to load before deciding to fallback
        if (workspaceLoading) return;

        const workspaceId = currentWorkspace?.id;
        if (workspaceId) {
          navigate(`/workspace/${workspaceId}/campaigns`, { replace: true });
        } else {
          navigate('/account/settings', { replace: true });
        }
      } finally {
        setRedirecting(false);
      }
    };

    redirect();
  }, [campaignId, location, currentWorkspace, navigate, workspaceLoading]);

  if (redirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-deep-900 dark:via-deep-900 dark:to-ocean-950 flex items-center justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  return null;
};

// Protected Route without Sidebar (for email builder)
const ProtectedRouteWithoutSidebar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  return (
    <OnboardingGuard>
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-900">
        <main className="min-h-screen">
          {children}
        </main>
      </div>
    </OnboardingGuard>
  );
};

// Public Route component (redirect to workspace dashboard if already logged in and not a new user)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isNewUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const [redirectPath, setRedirectPath] = React.useState<string | null>(null);

  const isShopifyInstallWizard = location.pathname.startsWith('/shopify/install');

  React.useEffect(() => {
    // Never bounce merchants out of the Shopify App Store install wizard
    if (isShopifyInstallWizard) {
      setRedirectPath(null);
      return;
    }

    // For brand-new users (just signed up), do NOT auto-redirect.
    // Let the onboarding / verification flow handle navigation.
    if (!currentUser || isNewUser) {
      return;
    }

    if (currentUser) {
      // Redirect to workspace dashboard if workspace exists, otherwise use utility
      if (currentWorkspace?.id) {
        setRedirectPath(`/workspace/${currentWorkspace.id}/dashboard`);
      } else {
        // Use getDashboardRedirectPath to ensure workspace exists
        import('./utils/workspaceRedirect').then(({ getDashboardRedirectPath }) => {
          getDashboardRedirectPath(currentUser.uid).then(path => {
            setRedirectPath(path);
          });
        });
      }
    }
  }, [currentUser, currentWorkspace, isShopifyInstallWizard, isNewUser]);

  if (currentUser && redirectPath && !isShopifyInstallWizard) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

// Landing page route (accessible to everyone)
const LandingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// Root redirect component - redirects based on authentication status
const RootRedirect: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { workspaces, loading: workspaceLoading, loadWorkspaces, isShopifyEmbedded } = useWorkspace();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shop = queryParams.get('shop');

  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Diagnostic logging
  useEffect(() => {
    console.log('📍 [TRACE] RootRedirect: Checking state', {
      authLoading,
      workspaceLoading,
      hasUser: !!currentUser,
      workspacesCount: (workspaces || []).length,
      shop,
      pathname: location.pathname,
      search: location.search
    });
  }, [authLoading, workspaceLoading, currentUser, workspaces, shop, location]);

  // Ensure workspaces are loaded if user is logged in
  useEffect(() => {
    if (currentUser && workspaces.length === 0 && !workspaceLoading) {
      loadWorkspaces();
    }
  }, [currentUser, workspaces.length, workspaceLoading, loadWorkspaces]);

  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        try {
          const userProfile = await userService.getUserProfile(currentUser.uid);
          setProfile(userProfile);
        } catch (error) {
          console.error('RootRedirect: Failed to fetch user profile', error);
        } finally {
          setProfileLoading(false);
        }
      } else {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, [currentUser]);

  if (authLoading || (currentUser && workspaceLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  // Handle Shopify launch specifically
  if (shop && location.pathname !== '/shopify/embedded') {
    console.log('RootRedirect: Shopify shop detected, redirecting to launch handler:', shop);
    return <Navigate to={`/shopify/embedded${location.search}`} replace />;
  }

  // Handle pending signup flow (from ShopifySignup)
  const pendingSignupMetadata = sessionStorage.getItem('shopify_pending_signup_metadata');
  if (currentUser && pendingSignupMetadata) {
    console.log('RootRedirect: Authenticated user with pending Shopify connection detected...');
    return <Navigate to="/oauth/callback/shopify" replace />;
  }

  // NEW: Handle restored Shopify connection after signup/login
  const pendingShopifyConnection = readShopifyInstallStorage(
    SHOPIFY_INSTALL_STORAGE_KEYS.pendingConnection
  );
  if (currentUser && pendingShopifyConnection) {
    try {
      const params = JSON.parse(pendingShopifyConnection);
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value as string);
      });
      
      console.log('RootRedirect: Restoring pending Shopify connection...', params.shop);
      removeShopifyInstallStorage(SHOPIFY_INSTALL_STORAGE_KEYS.pendingConnection);
      
      return <Navigate to={`/oauth/callback/shopify?${queryParams.toString()}`} replace />;
    } catch (e) {
      console.error('Failed to parse pending shopify connection', e);
      removeShopifyInstallStorage(SHOPIFY_INSTALL_STORAGE_KEYS.pendingConnection);
    }
  }

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <LoadingAnimation />
      </div>
    );
  }

  if (currentUser) {
    // Shopify install in progress — stay on install wizard, not embedded home
    const shopifyMetadata = readShopifyInstallStorage(SHOPIFY_INSTALL_STORAGE_KEYS.signupMetadata);
    if (shopifyMetadata && profile && needsShopifyInstallWizard(profile)) {
      try {
        const metadata = JSON.parse(shopifyMetadata);
        const wizardState = readShopifyInstallWizardState();
        const resumePath = getInstallWizardResumePath({
          profile,
          wizardState,
          needsWorkspacePicker: workspaces.length > 1 && !wizardState?.workspaceId,
          isAuthenticated: true,
        });
        return <Navigate to={resumePath} replace />;
      } catch (e) {
        console.error('Failed to parse shopify metadata', e);
      }
    }

    // ONBOARDING AWARE STEERING
    if (profile) {
      if (needsShopifyInstallWizard(profile)) {
        const wizardState = readShopifyInstallWizardState();
        const resumePath = getInstallWizardResumePath({
          profile,
          wizardState,
          needsWorkspacePicker: workspaces.length > 1 && !wizardState?.workspaceId,
          isAuthenticated: true,
        });
        return <Navigate to={resumePath} replace />;
      }

      if (requiresShopifyComplianceOnboarding(profile, isShopifyEmbedded)) {
        if (!profile.addressCompleted) {
          return <Navigate to={`/shopify/onboarding/address${location.search}`} replace />;
        }
        if (!profile.syncCompleted) {
          return <Navigate to={`/shopify/onboarding/sync${location.search}`} replace />;
        }
        if (!profile.brandingCompleted) {
          return <Navigate to={`/shopify/onboarding/branding${location.search}`} replace />;
        }
      }

      if (!profile.profileCompleted) {
        return <Navigate to="/onboarding/profile" replace />;
      }
      if (!profile.workspaceCompleted) {
        return <Navigate to="/onboarding/workspace" replace />;
      }
    }

    // If we have a workspace, go there
    if (workspaces.length > 0) {
      // Try to restore last used workspace if possible to avoid ID mismatch with context
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      const workspaceToUse = workspaces.find(w => w.id === savedWorkspaceId) || workspaces[0];
      
      console.log('RootRedirect: Redirecting to workspace dashboard:', workspaceToUse.name, workspaceToUse.id);
      return <Navigate to={`/workspace/${workspaceToUse.id}/dashboard`} replace />;
    }
    // Otherwise go to settings
    return <Navigate to="/account/settings" replace />;
  }

  // Not logged in and no shop param - go to login
  return <Navigate to="/login" replace />;
};

// ─── Public pages URL helper ──────────────────────────────────────────────────
export function getPagePublicUrl(slug: string, dev = false): string {
  if (dev) return `${window.location.origin}/p/${slug}`;
  return `https://pages.scubamail.io/${slug}`;
}

// Detect if running on pages.scubamail.io (the dedicated pages subdomain)
function isPagesSubdomain(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'pages.scubamail.io';
}

function App() {
  // ── pages.scubamail.io/rokit-5 → render the page at that path ────────────
  if (isPagesSubdomain()) {
    return (
      <ThemeProvider>
        <Router>
          <Suspense fallback={<LoadingAnimation />}>
            <Routes>
              {/* path is /:slug — the whole URL is the page identifier */}
              <Route path="/:pageSlug" element={<LandingPagePreviewBySlug />} />
              <Route path="/" element={<div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'system-ui',color:'#9ca3af',fontSize:14 }}>pages.scubamail.io</div>} />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    );
  }
  const isPublicFormRoute =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/form/') ||
      window.location.pathname.startsWith('/email-preview/') ||
      window.location.pathname.startsWith('/landing-page-preview/') ||
      window.location.pathname.startsWith('/p/') ||
      window.location.pathname.startsWith('/confirm-subscription') ||
      window.location.pathname.startsWith('/unsubscribe'));

  // Serve hosted public forms with a minimal app shell so we don't initialize
  // auth/workspace/admin providers (which create noisy logs and slower startup).
  if (isPublicFormRoute) {
    return (
      <ThemeProvider>
        <Router>
          <Suspense fallback={<LoadingAnimation />}>
            <Routes>
              <Route path="/form/:formId" element={<PublicSignupForm />} />
              <Route path="/email-preview/:previewId" element={<PublicEmailPreview />} />
              <Route path="/landing-page-preview/:pageId" element={<LandingPagePreviewById />} />
              <Route path="/p/:workspaceSlug/:pageSlug" element={<LandingPagePreviewBySlug />} />
              <Route path="/p/:pageSlug" element={<LandingPagePreviewBySlug />} />
              <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
              <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
              <Route path="*" element={<div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'system-ui',flexDirection:'column',gap:12 }}><p style={{fontSize:20,fontWeight:700,color:'#111',margin:0}}>Page not found</p><p style={{color:'#9ca3af',margin:0}}>This page does not exist.</p></div>} />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminProvider>
          <WorkspaceProvider>
            <NotificationProvider>
                    <Router>
                      <BillingProvider>
                <StorageProvider>
                  <SidebarProvider>
                      <ShopifyAppWrapper>
                        <div className="flex flex-col min-h-screen">
                          <GlobalUIElements />
                          <ClarityRouteTracker />
                          <AutomationSchedulerInitializer />
                          <SubdomainRouter />
                          <Suspense fallback={
                            <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
                              <LoadingAnimation />
                            </div>
                          }>
                          <Routes>
                            {/* Wrapper Route for Global Shopify Guard */}
                            <Route element={<ShopifyEmbeddedGuard children={<Outlet />} />}>
                              <Route path="/login" element={
                                <PublicRoute>
                                  <Login />
                                </PublicRoute>
                              } />
                              <Route path="/signup" element={
                                <PublicRoute>
                                  <Signup />
                                </PublicRoute>
                              } />
                          <Route path="/signup/shopify" element={
                            <PublicRoute>
                              <ShopifySignupRedirect />
                            </PublicRoute>
                          } />
                          <Route path="/shopify/install/*" element={
                            <PublicRoute>
                              <ShopifyInstallWizardRoutes />
                            </PublicRoute>
                          } />
                          <Route path="/shopify/onboarding/address" element={
                            <ProtectedRoute>
                              <ShopifyOnboardingAddress />
                            </ProtectedRoute>
                          } />
                          <Route path="/shopify/onboarding/sync" element={
                            <ProtectedRoute>
                              <ShopifyOnboardingSync />
                            </ProtectedRoute>
                          } />
                          <Route path="/shopify/onboarding/branding" element={
                            <ProtectedRoute>
                              <ShopifyOnboardingBrandingPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/shopify/integration" element={
                            <ProtectedRoute>
                              <ShopifyIntegrationDetails />
                            </ProtectedRoute>
                          } />
                          <Route path="/forgot-password" element={
                            <PublicRoute>
                              <ForgotPassword />
                            </PublicRoute>
                          } />
                          <Route path="/live-user-check" element={<LiveUserCheck />} />
                          <Route path="/verify-email" element={<EmailVerificationRequired />} />
                          <Route path="/verify-email-confirm" element={<EmailVerificationConfirm />} />
                          <Route path="/reset-password" element={<PasswordResetPasscode />} />
                          <Route path="/reset-password-confirm" element={<PasswordResetConfirm />} />
                          <Route path="/analytics" element={
                            <ProtectedRouteWithLayout>
                              <Analytics />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/analytics" element={
                            <ProtectedRouteWithLayout>
                              <Analytics />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/analytics/campaigns/:campaignId/report" element={
                            <ProtectedRouteWithLayout>
                              <CampaignPerformanceReportPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId" element={
                            <ProtectedRoute>
                              <WorkspaceDetails />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/dashboard" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceDashboardV2 />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts" element={
                            <ProtectedRouteWithLayout>
                              <Contacts />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/shopify-products" element={
                            <ProtectedRouteWithLayout>
                              <ShopifyStoreProducts />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/shopify/customers" element={
                            <ProtectedRouteWithLayout>
                              <ShopifyCustomersPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/shopify/orders" element={
                            <ProtectedRouteWithLayout>
                              <ShopifyOrdersPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/shopify/abandoned-carts" element={
                            <ProtectedRouteWithLayout>
                              <ShopifyAbandonedCartsPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/create" element={
                            <ProtectedRouteWithLayout>
                              <AddContact />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/segments" element={
                            <ProtectedRouteWithLayout>
                              <SegmentsPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/fields" element={
                            <ProtectedRouteWithLayout>
                              <ContactsFieldsPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/tags" element={
                            <ProtectedRouteWithLayout>
                              <ContactsTagsPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/import" element={
                            <ProtectedRouteWithLayout>
                              <ContactsImportPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/export" element={
                            <ProtectedRouteWithLayout>
                              <ContactsExportPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/hygiene" element={
                            <ProtectedRouteWithLayout>
                              <HygieneHub />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/:contactId" element={
                            <ProtectedRouteWithLayout>
                              <ContactView />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/contacts/import/map" element={
                            <ProtectedRouteWithLayout>
                              <ImportMapping />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/segment-suggestions" element={
                            <ProtectedRouteWithLayout>
                              <SegmentTemplateSelector />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/segment-builder" element={
                            <ProtectedRouteWithLayout>
                              <SegmentBuilderPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns" element={
                            <ProtectedRouteWithLayout>
                              <Campaigns />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/create" element={
                            <ProtectedRoute>
                              <CampaignCreateStart />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/whatsapp-setup" element={
                            <ProtectedRoute>
                              <WhatsAppCampaignSetup />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/whatsapp-templates" element={
                            <ProtectedRouteWithLayout>
                              <WhatsAppTemplates />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/name" element={
                            <ProtectedRoute>
                              <CampaignNamePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/setup" element={
                            <ProtectedRoute>
                              <CampaignSetup />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/html-editor" element={
                            <ProtectedRoute>
                              <HTMLEmailEditor />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/:campaignId/html-editor" element={
                            <ProtectedRoute>
                              <HTMLEmailEditor />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/templates" element={
                            <ProtectedRoute>
                              <TemplateGallery />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/:campaignId/campaignsummary" element={
                            <ProtectedRoute>
                              <CampaignSummary />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/:campaignId/tracking-analytics" element={
                            <ProtectedRouteWithLayout>
                              <CampaignTrackingAnalytics />
                            </ProtectedRouteWithLayout>
                          } />

                          <Route path="/workspace/:workspaceId/automations" element={
                            <ProtectedRouteWithLayout>
                              <Automations />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/automations/create" element={
                            <ProtectedRoute>
                              <AutomationCreatePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/automations/templates" element={
                            <ProtectedRoute>
                              <AutomationTemplatesPage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/automations/editor" element={
                            <ProtectedRoute>
                              <AutomationEditor />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/automations/edit/:automationId" element={
                            <ProtectedRoute>
                              <AutomationEditor />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/automations/test/:automationId" element={
                            <ProtectedRouteWithLayout>
                              <AutomationTester />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/analytics/automations/:automationId" element={
                            <ProtectedRouteWithLayout>
                              <AutomationAnalytics />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/templates" element={<Navigate to="/templates" replace />} />
                          <Route path="/workspace/:workspaceId/landing-pages/create" element={
                            <ProtectedRouteWithoutSidebar>
                              <LandingPageProjectSetup />
                            </ProtectedRouteWithoutSidebar>
                          } />
                          <Route path="/workspace/:workspaceId/landing-pages/editor" element={
                            <ProtectedRouteWithoutSidebar>
                              <LandingPageEditorShell />
                            </ProtectedRouteWithoutSidebar>
                          } />
                          <Route path="/workspace/:workspaceId/landing-pages/:pageId/dashboard" element={
                            <ProtectedRouteWithLayout>
                              <LandingPageDashboard />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/landing-pages" element={
                            <ProtectedRouteWithLayout>
                              <LandingPagesNew />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/signup-forms" element={
                            <ProtectedRouteWithLayout>
                              <SignupForms />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/signup-forms/new" element={
                            <ProtectedRoute>
                              <SignupFormNamePage />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/signup-forms/new/template" element={
                            <ProtectedRoute>
                              <SignupFormTemplateSelection />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/signup-forms/:formId/edit" element={
                            <ProtectedRouteWithLayout>
                              <SignupFormBuilder />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings" element={
                            <ProtectedRouteWithLayout>
                              <Navigate to="general" replace />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/developer" element={
                            <ProtectedRouteWithLayout>
                              <DeveloperSettings />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/senders" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceSettingsSendersIndexRedirect />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/senders/:subTab" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceSettings />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/:tab" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceSettings />
                            </ProtectedRouteWithLayout>
                          } />

                          {/* Workspace-specific Integration Routes */}
                          <Route path="/workspace/:workspaceId/integration" element={
                            <ProtectedRouteWithLayout>
                              <Integration />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/integration/shopify" element={
                            <ProtectedRouteWithLayout>
                              <ShopifyIntegrationDetails />
                            </ProtectedRouteWithLayout>
                          } />

                          {/* WooCommerce dedicated setup page */}
                          <Route path="/workspace/:workspaceId/integration/setup/woocommerce/connect" element={
                            <ProtectedRouteWithLayout>
                              <WooCommerceSetup />
                            </ProtectedRouteWithLayout>
                          } />
                          {/* Typeform dedicated setup page */}
                          <Route path="/workspace/:workspaceId/integration/setup/typeform" element={
                            <ProtectedRouteWithLayout>
                              <TypeformSetup />
                            </ProtectedRouteWithLayout>
                          } />
                          {/* Zapier dedicated hub */}
                          <Route path="/workspace/:workspaceId/integration/setup/zapier" element={
                            <ProtectedRouteWithLayout>
                              <ZapierIntegrationPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/integration/setup/:integrationSlug" element={
                            <ProtectedRouteWithLayout>
                              <IntegrationSetup />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/integration/zapier" element={
                            <ProtectedRouteWithLayout>
                              <ZapierIntegrationPage />
                            </ProtectedRouteWithLayout>
                          } />


                          <Route path="/account/profile" element={
                            <ProtectedRouteWithLayout>
                              <Profile />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/settings" element={
                            <ProtectedRouteWithLayout>
                              <AccountSettings />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/developer" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/developer`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/senders" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/senders`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/senders/verify-domain" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/senders/verify-domain`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/senders/verify-domain" element={
                            <ProtectedRouteWithLayout>
                              <VerifyDomain />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/dns-config" element={
                            <ProtectedRouteWithLayout>
                              <DomainDNSConfig />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/senders/dns-config" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect
                                resolvePath={(workspaceId, location) => `/workspace/${workspaceId}/dns-config${location.search ?? ''}`}
                              />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/senders/verify-email" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/senders/verify-email`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/senders/verify-email-address" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/senders/verify-email`} />
                            </ProtectedRouteWithLayout>
                          } />
                          {/* Redirect standalone senders page to settings/senders */}
                          <Route path="/workspace/:workspaceId/senders" element={
                            <ProtectedRouteWithLayout>
                              <Navigate to={`../settings/senders`} replace />
                            </ProtectedRouteWithLayout>
                          } />
                          {/* Redirect old verify-email to settings/senders/verify-email */}
                          <Route path="/workspace/:workspaceId/verify-email" element={
                            <ProtectedRouteWithLayout>
                              <Navigate to={`../settings/senders/verify-email`} replace />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/workspace/:workspaceId/settings/senders/verify-email" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceVerifyEmailAddress />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/activity" element={
                            <ProtectedRouteWithLayout>
                              <ActivityPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/settings" element={
                            <ProtectedRouteWithLayout>
                              <Settings />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/collections" element={
                            <ProtectedRouteWithLayout>
                              <CollectionManager />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/branding" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/branding`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/branding" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/settings/branding`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/users" element={
                            <ProtectedRouteWithLayout>
                              <Users />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/refer" element={
                            <ProtectedRouteWithLayout>
                              <Rewards />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/content-manager" element={
                            <ProtectedRouteWithLayout>
                              <ContentManager />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/billing" element={
                            <ProtectedRouteWithLayout>
                              <Profile />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/account/active-session" element={
                            <ProtectedRouteWithLayout>
                              <ActiveSession />
                            </ProtectedRouteWithLayout>
                          } />

                          <Route path="/signup-forms" element={
                            <ProtectedRouteWithLayout>
                              <SignupForms />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/landing-pages" element={
                            <ProtectedRouteWithLayout>
                              <LandingPagesNew />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/integration" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId) => `/workspace/${workspaceId}/integration`} />
                            </ProtectedRouteWithLayout>
                          } />

                          <Route path="/integration/setup/:integrationSlug" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect resolvePath={(workspaceId, location) => `/workspace/${workspaceId}/integration/setup${location.pathname.replace('/integration/setup', '')}`} />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/oauth/callback/:integrationSlug" element={<OAuthCallback />} />
                          <Route path="/oauth/zapier/authorize" element={<ZapierOAuthAuthorize />} />
                          <Route path="/shopify/embedded" element={<ShopifyEmbeddedApp />} />
                          <Route path="/shopify/billing-confirm" element={<ShopifyBillingConfirm />} />
                          <Route path="/forms-landing-pages" element={
                            <ProtectedRouteWithLayout>
                              <LandingPages />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/subdomains" element={
                            <ProtectedRouteWithLayout>
                              <SubdomainManager />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/subdomains/:subdomainId/build" element={
                            <ProtectedRouteWithLayout>
                              <SubdomainLandingPageBuilder />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/subdomains/:subdomainId/build/:pageId" element={
                            <ProtectedRouteWithLayout>
                              <SubdomainLandingPageBuilder />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/style" element={
                            <ProtectedRouteWithLayout>
                              <FormStyleSelection />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/template" element={
                            <ProtectedRouteWithLayout>
                              <TemplateSelectionPage />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/content" element={
                            <ProtectedRouteWithLayout>
                              <FormContentConfig />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/landing-template" element={
                            <ProtectedRouteWithLayout>
                              <LandingPageTemplateSelection />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/landing-content" element={
                            <ProtectedRouteWithLayout>
                              <LandingPageContentConfig />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/design" element={
                            <ProtectedRouteWithLayout>
                              <LandingPageBuilder />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/forms-landing-pages/create/enhanced-design" element={
                            <ProtectedRouteWithLayout>
                              <EnhancedLandingPageBuilder />
                            </ProtectedRouteWithLayout>
                          } />
                          {/* Redirect standalone /campaigns routes to workspace campaigns */}
                          <Route path="/campaigns" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect
                                resolvePath={(workspaceId) => `/workspace/${workspaceId}/campaigns`}
                                fallback="/account/settings"
                              />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/campaigns/calendar" element={
                            <ProtectedRouteWithLayout>
                              <WorkspaceRouteRedirect
                                resolvePath={(workspaceId) => `/workspace/${workspaceId}/campaigns`}
                                fallback="/account/settings"
                              />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/campaigns/setup" element={
                            <ProtectedRoute>
                              <WorkspaceRouteRedirect
                                resolvePath={(workspaceId) => `/workspace/${workspaceId}/campaigns/setup`}
                                fallback="/account/settings"
                              />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/:campaignId/design" element={
                            <ProtectedRouteWithoutSidebar>
                              <TemplateBuilder />
                            </ProtectedRouteWithoutSidebar>
                          } />
                          <Route path="/campaigns/:campaignId/design" element={
                            <ProtectedRoute>
                              <CampaignRouteRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/campaigns/:campaignId/content" element={
                            <ProtectedRoute>
                              <CampaignRouteRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/workspace/:workspaceId/campaigns/:campaignId/analytics" element={
                            <ProtectedRouteWithLayout>
                              <CampaignAnalytics />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/campaigns/:campaignId/send" element={
                            <ProtectedRoute>
                              <CampaignRouteRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/campaigns/:campaignId/edit" element={
                            <ProtectedRoute>
                              <CampaignRouteRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/templates" element={
                            <ProtectedRouteWithLayout>
                              <TemplatesGallery />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/templates/:id/edit" element={
                            <ProtectedRouteWithLayout>
                              <TemplateEditor />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/templates/:id/preview" element={
                            <ProtectedRouteWithLayout>
                              <TemplatePreview />
                            </ProtectedRouteWithLayout>
                          } />
                          <Route path="/templates/builder" element={
                            <ProtectedRoute>
                              <TemplateBuilder />
                            </ProtectedRoute>
                          } />
                          
                          {/* Redirect legacy automation routes to workspace-scoped versions */}
                          <Route path="/automations" element={
                            <WorkspaceRouteRedirect 
                              resolvePath={(workspaceId) => `/workspace/${workspaceId}/automations`} 
                            />
                          } />
                          <Route path="/automations/create" element={
                            <WorkspaceRouteRedirect 
                              resolvePath={(workspaceId) => `/workspace/${workspaceId}/automations/create`} 
                            />
                          } />
                          <Route path="/automations/templates" element={
                            <WorkspaceRouteRedirect 
                              resolvePath={(workspaceId) => `/workspace/${workspaceId}/automations/templates`} 
                            />
                          } />
                          <Route path="/automations/editor" element={
                            <WorkspaceRouteRedirect 
                              resolvePath={(workspaceId) => `/workspace/${workspaceId}/automations/editor`} 
                            />
                          } />
                          <Route path="/automations/edit/:automationId" element={
                            <WorkspaceRouteRedirect 
                              resolvePath={(workspaceId, location) => {
                                const automationId = location.pathname.split('/').pop();
                                return `/workspace/${workspaceId}/automations/edit/${automationId}`;
                              }} 
                            />
                          } />
                          
                          <Route path="/workspace/:workspaceId/templates" element={<Navigate to="/templates" replace />} />
                          <Route path="/welcome-to-scubamail" element={
                            <ProtectedRoute>
                              <WelcomeSignup />
                            </ProtectedRoute>
                          } />
                          <Route path="/onboarding/profile" element={
                            <ProtectedRoute>
                              <OnboardingProfile />
                            </ProtectedRoute>
                          } />
                          <Route path="/onboarding/workspace" element={
                            <ProtectedRoute>
                              <OnboardingWorkspace />
                            </ProtectedRoute>
                          } />
                          <Route path="/onboarding/plan" element={
                            <ProtectedRoute>
                              <OnboardingPlanRedirect />
                            </ProtectedRoute>
                          } />
                          <Route path="/checkout" element={
                            <ProtectedRoute>
                              <Checkout />
                            </ProtectedRoute>
                          } />
                          <Route path="/dodo-checkout-return" element={
                            <ProtectedRoute>
                              <DodoCheckoutReturn />
                            </ProtectedRoute>
                          } />
                          <Route path="/welcome" element={
                            <ProtectedRoute>
                              <WelcomePage />
                            </ProtectedRoute>
                          } />
                          {/* Admin Routes */}
                          <Route path="/admin" element={
                            <PublicRoute>
                              <AdminIndex />
                            </PublicRoute>
                          } />
                          <Route path="/admin/login" element={<AdminLogin />} />
                          <Route path="/admin/control-panel-7x9k2m" element={
                            <Navigate to="/admin/control-panel-7x9k2m/overview" replace />
                          } />

                          {/* Admin Dashboard - Create email templates route (needs to be before generic :page route) */}
                          <Route path="/admin/control-panel-7x9k2m/create-email-templates" element={
                            <AdminRouteWrapper>
                              <AdminCreateEmailTemplatePage />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/control-panel-7x9k2m/create-email-templates/:templateId" element={
                            <AdminRouteWrapper>
                              <AdminCreateEmailTemplatePage />
                            </AdminRouteWrapper>
                          } />

                          {/* Admin Dashboard - Template details route (needs to be before generic :page route) */}
                          <Route path="/admin/control-panel-7x9k2m/email-templates/:templateId/details" element={
                            <AdminRouteWrapper>
                              <AdminTemplateDetailsPage />
                            </AdminRouteWrapper>
                          } />

                          {/* Admin Dashboard - Signup form editor route */}
                          <Route path="/admin/control-panel-7x9k2m/signup-forms/:formId/edit" element={
                            <AdminRouteWrapper>
                              <SignupFormBuilder />
                            </AdminRouteWrapper>
                          } />

                          {/* Admin Dashboard - Template editor route */}
                          <Route path="/admin/control-panel-7x9k2m/email-templates/:templateId/edit" element={
                            <AdminRouteWrapper>
                              <AdminTemplateBuilder />
                            </AdminRouteWrapper>
                          } />

                          {/* Admin Dashboard - All pages with URL slugs */}
                          <Route path="/admin/control-panel-7x9k2m/:page" element={
                            <AdminRouteWrapper>
                              <AdminDashboardLayout />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/user-contacts/:userId" element={
                            <AdminRouteWrapper>
                              <UserContacts />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/control-panel-7x9k2m/user-details/:userId" element={
                            <AdminRouteWrapper>
                              <UserDetailsPage />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/user-details/:userId" element={
                            <AdminRouteWrapper>
                              <UserDetailsPage />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/templates" element={
                            <AdminRouteWrapper>
                              <AdminTemplateManager />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/dns-checker" element={
                            <AdminRouteWrapper>
                              <StandaloneDNSChecker />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/automation-template-editor" element={
                            <AdminRouteWrapper>
                              <AdminAutomationTemplateEditorPage />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/automation-template-workflow" element={
                            <AdminRouteWrapper>
                              <AutomationEditor />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/platform-composer" element={
                            <AdminRouteWrapper>
                              <PlatformEmailComposer />
                            </AdminRouteWrapper>
                          } />
                          <Route path="/admin/template-audit" element={
                            <AdminRouteWrapper>
                              <TemplateAuditPage />
                            </AdminRouteWrapper>
                          } />

                          <Route path="/notifications" element={
                            <ProtectedRouteWithLayout>
                              <NotificationPage />
                            </ProtectedRouteWithLayout>
                          } />

                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/api-documentation" element={<ApiPage />} />
                          <Route path="/help" element={<HelpCenter />} />
                          <Route path="/help/article/:slug" element={<HelpArticleDetail />} />
                          <Route path="/help/category/:categoryId" element={<HelpCategoryPage />} />
                          <Route path="/help/search" element={<HelpSearchResults />} />

                          {/* Legal and Policy pages */}
                          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                          <Route path="/anti-spam-policy" element={<AntiSpamPolicyPage />} />
                          <Route path="/cancellation-refund" element={<CancellationRefundPage />} />
                          <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                          <Route path="/status" element={<StatusPage />} />

                          {/* Email tracking routes - must be before subdomain routes */}
                          <Route path="/track/open" element={<TrackingHandler />} />
                          <Route path="/track/click" element={<TrackingHandler />} />

                          {/* Public subdomain routes - must be before the root route */}
                          <Route path="/subdomain/:subdomain/:slug" element={<PublicLandingPageWrapper />} />
                          <Route path="/subdomain/:subdomain" element={<PublicLandingPageWrapper />} />
                          {/* Landing page preview — internal (by Firestore ID, works for draft + published) */}
                          <Route path="/landing-page-preview/:pageId" element={<LandingPagePreviewById />} />
                          <Route path="/p/:workspaceSlug/:pageSlug" element={<LandingPagePreviewBySlug />} />
                          <Route path="/p/:pageSlug" element={<LandingPagePreviewBySlug />} />

                          {/* Public Signup Form Routes */}
                          <Route path="/form/:formId" element={<PublicSignupForm />} />
                          <Route path="/email-preview/:previewId" element={<PublicEmailPreview />} />
                          <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
                          <Route path="/unsubscribe" element={<PublicUnsubscribe />} />

                          {/* Root route - redirects based on authentication */}
                          <Route path="/" element={<RootRedirect />} />
                          {/* 404 Route - Must be last */}
                          <Route path="*" element={<NotFound />} />
                            </Route>
                          </Routes>
                          </Suspense>
                      </div>
                    </ShopifyAppWrapper>
                </SidebarProvider>
                </StorageProvider>
                      </BillingProvider>
                    </Router>
              <NotificationPopup />
            </NotificationProvider>
          </WorkspaceProvider>
        </AdminProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Helper component to exclude global UI elements from certain pages
const GlobalUIElements: React.FC = () => {
  const location = useLocation();
  const { isShopifyEmbedded } = useWorkspace();
  const isPublicStatsPage = location.pathname === '/live-user-check';
  const isOnboardingPage =
    location.pathname.startsWith('/onboarding') ||
    location.pathname.startsWith('/shopify/onboarding');
  const isWelcomeSignupPage = location.pathname === '/welcome-to-scubamail';

  if (isPublicStatsPage || isShopifyEmbedded) return null;

  return (
    <>
      <SessionController />
    </>
  );
};

export default App;
export { };
